import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api, ApiError, type ScriptProgress } from '../lib/api.js';
import { useCan, useRefusal } from '../lib/permissions.js';
import { useScriptRun } from '../lib/useScriptRun.js';

import { Callout } from './Callout.js';
import { AiCostApproval } from './credits/AiCostApproval.js';
import { CreditBalanceChip } from './credits/CreditBalanceChip.js';
import { aiBillingMode, refusalCode, useProjectCredits } from './credits/useProjectCredits.js';

/**
 * HAS THE SCREENPLAY EVER BEEN READ BY A MODEL?
 *
 * Not the same question as "did the run finish", and the two were conflated
 * everywhere until 2026-10-26. Parsing is automatic and writes a COMPLETED run
 * with a PARSE stage; extraction is a button somebody presses and adds an
 * EXTRACT stage to a run of its own. So a production that skipped the button
 * has a COMPLETED run, zero requirements, and every screen saying the AI looked
 * and found nothing — which is a statement about the producer's screenplay that
 * nobody is entitled to make.
 *
 * `progress` is the LATEST run for the version (`ScriptsService.progressFor`),
 * so this answers "has the most recent reading of this draft included an
 * extraction", which is the question each surface actually asks before it
 * explains an empty breakdown.
 */
/**
 * How many scenes the last run could not carry, because the screenplay is
 * longer than one request holds.
 *
 * Read off the EXTRACT stage rather than guessed from the requirement count: a
 * scene with no requirement may have been read and found to need no location,
 * and the two must never be told as the same thing. The pipeline marks the
 * stage PARTIAL with its own code for exactly this
 * (`SCENES_DID_NOT_FIT_FAILURE_CODE`, `script-extract.pipeline.ts`), because a
 * bare PARTIAL already meant "the model refused some of it" — a different fact
 * needing a different sentence.
 */
export function scenesThatDidNotFit(progress: ScriptProgress | null): number {
  const stage = progress?.stages.find((s) => s.stage === 'EXTRACT');

  return stage?.failureCode === 'SCENES_DID_NOT_FIT' ? stage.failedUnits : 0;
}

/**
 * How many scenes the last run read for locations but not for every department.
 *
 * The scene breakdown is about a hundred requests, and one that gets no answer
 * leaves its scenes without some of their elements. The pipeline marks the
 * stage PARTIAL with its own code (`ELEMENTS_NOT_READ_FAILURE_CODE`,
 * `script-extract.pipeline.ts`): an empty Props row on such a scene says nothing
 * about the scene, and the producer must be told so, not left to read it as
 * "no props".
 */
export function scenesWithoutElements(progress: ScriptProgress | null): number {
  const stage = progress?.stages.find((s) => s.stage === 'EXTRACT');

  return stage?.failureCode === 'ELEMENTS_NOT_READ' ? stage.failedUnits : 0;
}

export function extractionHasRun(progress: ScriptProgress | null): boolean {
  if (progress === null) return false;
  if (progress.stages.some((s) => s.stage === 'EXTRACT')) return true;

  /**
   * A FAILED run with no stage at all counts as an attempt. The stage row is
   * written by `ScriptExtractPipeline.run()` after it claims the run, so a job
   * that died earlier — or a re-delivery that closed a stranded run
   * `EXTRACT_INTERRUPTED` — can leave a failure with no stage behind it.
   * Reading that as "never read" would put "this screenplay has not been read
   * yet" directly underneath "the AI breakdown did not finish".
   *
   * BUT A FAILED PARSE IS NOT AN EXTRACTION. Parse and extract are separate
   * runs, and a parse writes its PARSE stage before it starts, so a failed run
   * carrying one is the screenplay failing to be READ — no model was ever
   * asked. Counting it here offered "Analyse again" for a script with no
   * scenes to analyse (since 2026-10-27 a parse that recognises no heading
   * fails `NO_SCENES_FOUND` instead of completing empty).
   */
  return progress.status === 'FAILED' && !progress.stages.some((s) => s.stage === 'PARSE');
}

/**
 * WHY THE SCREENPLAY COULD NOT BE READ, when that is what happened.
 *
 * `null` for anything but a failed parse. The codes are the parser's own
 * (`ExtractionFailed.code` in the backend), recorded since 2026-10-27 rather
 * than the class name every one of them used to share.
 */
export function parseFailure(progress: ScriptProgress | null): string | null {
  if (progress?.status !== 'FAILED') return null;

  const parse = progress.stages.find((s) => s.stage === 'PARSE');

  return parse ? (parse.failureCode ?? progress.failureCode ?? 'UNKNOWN') : null;
}

/**
 * WHETHER THE BREAKDOWN REVIEW SHOULD OFFER A READING AT ALL.
 *
 * Three reasons, and none of them is "the page is empty". A screenplay too long
 * for one request comes back with requirements for its first part and none for
 * the rest, so the count is not zero and those scenes were still never read —
 * deciding on the count alone leaves that producer with no way to ask for them.
 * And a reading that FAILED produced nothing, so offering it again beside the
 * earlier results overwrites nobody's work: the remedy for a failed run is the
 * run (both workflow pages promise it, owner brief 2026-10-29).
 *
 * Past those, the page is for correcting what the model produced: a "read it
 * again" button beside reviewed work invites paying a second time to overwrite
 * corrections somebody made by hand.
 */
export function shouldOfferAnalysis(input: {
  requirementCount: number;
  scenesDidNotFit: number;
  /** Scenes read for locations whose elements did not all come back (`scenesWithoutElements`). */
  scenesWithoutElements?: number;
  /** The last AI reading failed — not a parse failure, which the model never saw. */
  lastRunFailed?: boolean;
}): boolean {
  return (
    input.requirementCount === 0 ||
    input.scenesDidNotFit > 0 ||
    (input.scenesWithoutElements ?? 0) > 0 ||
    input.lastRunFailed === true
  );
}

/**
 * THE ONE WAY TO START AN AI BREAKDOWN, wherever a producer meets the gap.
 *
 * It lived only in the Script tab's own panel, which the guided setup flow
 * never mentions and never links to — so a producer who walked steps 1 → 2 → 3
 * reached "0 location requirements across 120 scenes" with no control on the
 * page that could change that, and step 2 still carried a comment saying step 1
 * starts the breakdown, which stopped being true when extraction became a
 * button. One component, rendered by every surface that can be the place
 * somebody notices.
 *
 * It owns the refusals too, because they are the same three everywhere: a
 * second breakdown while one is in flight is not a failure, a plan without the
 * AI scout is not an error to retry, and anything else is.
 *
 * STEP 1 OFFERS NONE (owner brief, 2026-10-29). Its own panel sat beside
 * "Review scenes" as a second red button on a screen whose job was done; the
 * breakdown belongs after the scenes have been looked at, so step 2 — this
 * component — is where a producer on the guided path first meets it.
 */
export function AnalyseWithAi({
  projectId,
  versionId,
  sceneCount,
  sceneIds,
  onStarted,
  testId = 'analyse-panel',
  reads = 'LOCATIONS',
}: {
  projectId: string;
  versionId: string | null;
  /** Nothing to read is a real reason to refuse; an unknown count is not. */
  sceneCount: number | undefined;
  /**
   * Read only these scenes. Absent is the whole screenplay, which is every
   * ordinary run; a selection is how the rest of a screenplay too long for one
   * request gets read, and how a producer re-reads a part they corrected.
   */
  sceneIds?: readonly string[];
  /** Fired once the run is enqueued — the moment a selection has served its purpose. */
  onStarted?: () => void;
  testId?: string;
  /**
   * The workflow asking. The reading is ONE run whatever asks for it — it
   * proposes the location requirements and, on the same run, every scene's
   * elements (the cast from the character names and from the model) — so only
   * the words change, each workflow told first about what it will review.
   */
  reads?: 'LOCATIONS' | 'ELEMENTS';
}) {
  const queryClient = useQueryClient();

  /**
   * Read-only here: `inFlight` is what decides whether to offer the button at
   * all. The re-read when a run ENDS stays with the screens that own the data
   * on them — `ReviewScenesStep` and `ScriptPanel` both pass `onFinished` to
   * their own `useScriptRun`, and adding a third copy of that invalidation
   * would fire it twice for one finish.
   */
  const { inFlight, progress } = useScriptRun(versionId);

  /**
   * METERED AI ASKS FIRST (design B.5, S6). Where the platform stands comes
   * from the wallet: LEGACY is this component exactly as it was; METERED opens
   * the cost approval instead of calling the legacy verb; SUSPENDED starts
   * nothing. Unknown reads as LEGACY, and the server refuses the legacy verb of
   * a metered production with `BILLING_APPROVAL_REQUIRED` — which opens the
   * approval too, so the gate is the server's either way.
   */
  const credits = useProjectCredits(projectId);
  const canRun = useCan('AI_RUN');
  const runRefusal = useRefusal('AI_RUN');
  // The approval panel's mount key; null while it is closed.
  const [approval, setApproval] = useState<number | null>(null);
  // What the server said about the legacy verb, kept: it outlives the refusal.
  const [serverSaid, setServerSaid] = useState<'METERED' | 'SUSPENDED' | null>(null);
  // An approved run, once its progress shows: the panel gives way to "Reading…".
  const [meteredStarted, setMeteredStarted] = useState(false);

  useEffect(() => {
    // The run is visibly under way, so the approval has done its job; leaving it
    // open would also leave this control without its button once the run ends.
    if (meteredStarted && inFlight) setApproval(null);
  }, [meteredStarted, inFlight]);

  const ranOrStarted = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['requirements', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['review-progress', projectId] }),
      // The run row is what every surface polls to show "still reading"; the
      // answer on screen predates the job that was just enqueued.
      queryClient.invalidateQueries({ queryKey: ['script-progress', versionId] }),
    ]);

    onStarted?.();
  };

  const analyse = useMutation({
    mutationFn: () => api.requestExtraction(projectId, versionId as string, sceneIds),
    onSuccess: ranOrStarted,
    onError: (error: Error) => {
      // Metered after all (the wallet had not said so yet): ask, as metered does.
      if (refusalCode(error) === 'BILLING_APPROVAL_REQUIRED') {
        setServerSaid('METERED');
        setApproval((n) => (n ?? 0) + 1);
        void credits.refetch();
      }
      if (refusalCode(error) === 'AI_BILLING_UNAVAILABLE') {
        setServerSaid('SUSPENDED');
        void credits.refetch();
      }
    },
  });

  if (versionId === null) return null;

  const legacyRefusal = refusalCode(analyse.error);
  const mode = aiBillingMode(credits.data);
  const paused = mode === 'SUSPENDED' || (mode !== 'METERED' && serverSaid === 'SUSPENDED');
  const metered = !paused && (mode === 'METERED' || serverSaid === 'METERED');
  const approving = metered && !paused && approval !== null;
  // Under metering a member without AI_RUN is not offered the run (S6); before
  // it, the button stays as it always was and the server explains a refusal.
  const runHidden = paused || approving || (metered && !canRun);

  const noScenes = sceneCount === 0;

  /**
   * THE WORDING IS A CLAIM ABOUT THE PRODUCER'S SCREENPLAY, so it follows the
   * evidence rather than the caller. A surface showing an empty breakdown
   * cannot know which of the two emptinesses it is looking at, and guessing
   * wrong is the exact defect this component was added for: telling somebody
   * the AI read their script and found no locations, when nobody ever asked it
   * to.
   */
  const everRan = extractionHasRun(progress);
  const didNotFit = scenesThatDidNotFit(progress);
  const withoutElements = scenesWithoutElements(progress);
  const picked = sceneIds?.length ?? 0;

  const heading = picked > 0
    ? `Analyse ${String(picked)} selected ${picked === 1 ? 'scene' : 'scenes'}`
    : didNotFit > 0
      ? `${String(didNotFit)} ${didNotFit === 1 ? 'scene was' : 'scenes were'} not read`
      : withoutElements > 0
        ? `${String(withoutElements)} ${withoutElements === 1 ? 'scene was' : 'scenes were'} not fully read`
        : everRan
          ? 'AI breakdown'
          : 'This screenplay has not been read yet';

  const intro = picked > 0
    ? 'A paid reading of the scenes picked: their locations and their elements. Everything already found stays as it is.'
    : didNotFit > 0
      ? 'This screenplay is longer than one request can carry, so the end of it was never sent — those scenes were not examined and found empty, they were not read at all. Pick them in the scene list and run them as their own request; what has already been found stays as it is.'
      : withoutElements > 0
        ? 'The AI got no answer for some departments of these scenes, so their breakdown is missing elements — an empty row there does not mean the scene needs nothing. Pick the scenes and run them again, or tag what is missing by hand; what has already been found stays as it is.'
        : reads === 'ELEMENTS'
          ? 'A paid AI reading proposes, scene by scene, what each department needs: cast, background, props, costumes, vehicles, effects and the rest, each with the words of the script it comes from. You confirm or remove each proposal and tag anything it missed. It takes a few minutes for a feature, and it is never run for you.'
          : everRan
            ? 'Reads the whole screenplay and works out the locations the production needs, and what each scene needs on set — a few minutes for a feature-length draft. It is a paid reading, so it is never run for you: press it when this is the draft you want read.'
            : 'Its scenes were found by parsing, which is free and automatic. Working out the locations, and what each scene needs on set, is a paid reading, so it waits for somebody to ask — press this and the proposals arrive together when the reading finishes.';

  return (
    <div data-testid={testId}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
          padding: '14px 16px',
          background: 'var(--color-surface)',
          border: '1px solid var(--color-divider)',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p className="colh" style={{ margin: 0 }}>
            {heading}
          </p>
          <p className="mm" style={{ fontSize: 12.5, margin: '4px 0 0', lineHeight: 1.6 }}>
            {/* SAY WHAT IT COSTS BEFORE IT IS SPENT — and say it CORRECTLY.
                This read "one request per scene", which the pipeline stopped
                doing: locations are extracted in ONE request for the whole
                screenplay, because asking scene by scene cannot see that scene
                1 and scene 74 are the same apartment. The old sentence priced
                the feature at a hundred times what it costs, and it was also
                the argument for the button being manual. Since 2026-11-03 the
                same run also reads each scene's elements, in groups of scenes,
                so it is minutes, not seconds, and the wording says so. */}
            {intro}
            {metered && !paused ? ' You see what it costs before anything runs.' : ''}
          </p>
        </div>

        {metered && !paused ? (
          <span style={{ marginLeft: 'auto' }}>
            <CreditBalanceChip projectId={projectId} />
          </span>
        ) : null}

        {runHidden ? null : (
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginLeft: metered ? undefined : 'auto' }}
            data-testid="analyse-with-ai"
            disabled={analyse.isPending || inFlight || sceneCount === undefined || noScenes}
            onClick={() => {
              if (metered) {
                // The cost first; nothing runs until it is approved.
                analyse.reset();
                setMeteredStarted(false);
                setApproval((n) => (n ?? 0) + 1);
                return;
              }
              analyse.mutate();
            }}
          >
            {inFlight
              ? 'Reading…'
              : analyse.isPending
                ? 'Starting…'
                : picked > 0
                  ? `Analyse ${String(picked)} ${picked === 1 ? 'scene' : 'scenes'}`
                  : everRan
                    ? 'Analyse again'
                    : 'Analyse with AI'}
          </button>
        )}
      </div>

      {paused ? (
        <div style={{ marginTop: 16 }}>
          <Callout tone="warn" title="AI analysis is paused" testId="analyse-paused">
            New AI work cannot be started right now. Anything already running finishes and is charged as
            usual, and the credits on this production are safe.
          </Callout>
        </div>
      ) : null}

      {metered && !paused && !canRun ? (
        <p className="mm" style={{ fontSize: 12.5, margin: '8px 0 0' }} data-testid="analyse-not-allowed">
          {runRefusal ?? 'Your role on this production does not run AI.'}
        </p>
      ) : null}

      {approving ? (
        <AiCostApproval
          key={approval}
          projectId={projectId}
          operationType="SCRIPT_ANALYSIS"
          scriptVersionId={versionId}
          sceneIds={sceneIds}
          testId="analyse-approval"
          onClose={() => {
            setApproval(null);
          }}
          onStarted={() => {
            setMeteredStarted(true);
            void ranOrStarted();
          }}
        />
      ) : null}

      {noScenes ? (
        <p className="mm" style={{ fontSize: 12.5, margin: '8px 0 0' }} data-testid="analyse-no-scenes">
          There are no scenes to read yet.
        </p>
      ) : null}

      {analyse.isSuccess || (meteredStarted && !approving) ? (
        <div style={{ marginTop: 16 }}>
          <Callout tone="ok" title="Analysis started" testId="analyse-started">
            It runs in the background and everything it finds arrives together when it finishes. You
            can leave this page.
          </Callout>
        </div>
      ) : null}

      {analyse.isError && legacyRefusal !== 'BILLING_APPROVAL_REQUIRED' && legacyRefusal !== 'AI_BILLING_UNAVAILABLE' ? (
        (() => {
          const code = analyse.error instanceof ApiError ? analyse.error.code : null;
          /* ALREADY RUNNING IS NOT A FAILURE. The server refuses a second
             breakdown of the same screenplay while one is in flight, which is
             what stops a double-click buying two readings — but to the producer
             it must read as "it is already happening", with the progress shown
             elsewhere on the page, rather than as something that went wrong. */
          const running = code === 'EXTRACTION_ALREADY_RUNNING';
          const needsPlan = code === 'AI_NOT_IN_PLAN';

          return (
            <div style={{ marginTop: 16 }}>
              <Callout
                tone={running ? 'info' : needsPlan ? 'warn' : 'danger'}
                title={
                  running
                    ? 'This screenplay is already being read'
                    : needsPlan
                      ? 'The AI scout is not in this production’s plan'
                      : 'Could not start the analysis'
                }
                testId="analyse-error"
              >
                {analyse.error.message}
              </Callout>
            </div>
          );
        })()
      ) : null}
    </div>
  );
}
